import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Archive, XCircle } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

export function LossReasonsSection() {
  const listQuery = trpc.crm.lossReasons.list.useQuery({});
  const utils = trpc.useUtils();
  const create = trpc.crm.lossReasons.create.useMutation({
    onSuccess: () => {
      utils.crm.lossReasons.list.invalidate();
      toast.success("Motivo adicionado");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao adicionar motivo")),
  });
  const update = trpc.crm.lossReasons.update.useMutation({
    onSuccess: () => utils.crm.lossReasons.list.invalidate(),
  });
  const archive = trpc.crm.lossReasons.archive.useMutation({
    onSuccess: () => {
      utils.crm.lossReasons.list.invalidate();
      toast.success("Motivo arquivado");
    },
  });
  const [newName, setNewName] = useState("");

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <XCircle className="h-4 w-4" /> Motivos de perda
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Mostrados quando uma oportunidade é marcada como perdida.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2">
          <Input
            placeholder="Novo motivo (ex: Preço fora do orçamento)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) {
                create.mutate(
                  { name: newName.trim() },
                  { onSuccess: () => setNewName("") },
                );
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={() => {
              if (!newName.trim()) return;
              create.mutate(
                { name: newName.trim() },
                { onSuccess: () => setNewName("") },
              );
            }}
            disabled={create.isPending || !newName.trim()}
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar
          </Button>
        </div>
        {(listQuery.data ?? []).length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground border-2 border-dashed rounded-lg mt-2">
            Nenhum motivo cadastrado.
          </div>
        ) : (
          <div className="space-y-1.5 pt-1">
            {(listQuery.data ?? []).map((r: any) => (
              <div
                key={r.id}
                className="group flex items-center gap-2 border rounded-md px-3 py-2 hover:bg-muted/30"
              >
                <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                <Input
                  className="h-8 flex-1 border-transparent shadow-none focus:border-input focus:shadow-sm"
                  defaultValue={r.name}
                  onBlur={(e) => {
                    if (e.target.value.trim() && e.target.value !== r.name) {
                      update.mutate({ id: r.id, name: e.target.value.trim() });
                    }
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100"
                  onClick={() => archive.mutate({ id: r.id })}
                  title="Arquivar"
                >
                  <Archive className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
